import {
  type KeyboardEvent,
  type PointerEvent,
  useCallback,
  useRef,
  useState,
} from 'react'

import { useUiStore } from '../../../shared/hooks'
import { SIDEBAR } from '../sidebar.constants'

const clamp = (w: number) => Math.min(SIDEBAR.max, Math.max(SIDEBAR.min, w))

/**
 * Drag, keyboard and double-click handling of the sidebar's resize handle.
 * Narrower than `SIDEBAR.snap` collapses to the icon rail; dragging out of
 * the rail expands again. While dragging, plugin frames ignore the pointer
 * (they would swallow the moves), see `body[data-resizing]` in app.css.
 */
export function useSidebarResize(defaultWidth: () => number) {
  const { sidebarWidth, navCollapsed, setSidebarWidth, setNavCollapsed } =
    useUiStore()

  const [dragging, setDragging] = useState(false)
  const left = useRef(0)
  /** The width before this drag: collapsing keeps it for "expand". */
  const before = useRef<{ width: number | null } | null>(null)

  const current = navCollapsed ? SIDEBAR.rail : (sidebarWidth ?? defaultWidth())

  const apply = useCallback(
    (raw: number) => {
      if (raw < SIDEBAR.snap) {
        setNavCollapsed(true)
        // Passing through narrower widths must not become the expanded width.
        if (before.current) setSidebarWidth(before.current.width)

        return
      }

      setNavCollapsed(false)
      setSidebarWidth(clamp(raw))
    },
    [setNavCollapsed, setSidebarWidth],
  )

  const reset = () => {
    setNavCollapsed(false)
    setSidebarWidth(null)
  }

  const onPointerDown = (e: PointerEvent<HTMLElement>) => {
    if (e.button !== 0) return
    e.preventDefault()
    left.current = e.currentTarget.parentElement!.getBoundingClientRect().left
    before.current = { width: sidebarWidth }
    e.currentTarget.setPointerCapture(e.pointerId)
    document.body.dataset.resizing = 'true'
    setDragging(true)
  }

  const onPointerMove = (e: PointerEvent<HTMLElement>) => {
    if (dragging) apply(e.clientX - left.current)
  }

  const end = (e: PointerEvent<HTMLElement>) => {
    if (!dragging) return
    e.currentTarget.releasePointerCapture(e.pointerId)
    delete document.body.dataset.resizing
    before.current = null
    setDragging(false)
  }

  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    const step = SIDEBAR.step * (e.shiftKey ? 4 : 1)

    const keys: Record<string, () => void> = {
      ArrowLeft: () => apply(current - step),
      ArrowRight: () =>
        apply(navCollapsed ? (sidebarWidth ?? defaultWidth()) : current + step),
      Home: () => setNavCollapsed(true),
      End: () => apply(SIDEBAR.max),
      Enter: () => reset(),
    }

    if (keys[e.key]) {
      e.preventDefault()
      keys[e.key]!()
    }
  }

  return {
    width: current,
    dragging,
    collapsed: navCollapsed,
    custom: navCollapsed || sidebarWidth !== null,
    toggle: () => setNavCollapsed(!navCollapsed),
    reset,
    handle: {
      onPointerDown,
      onPointerMove,
      onPointerUp: end,
      onPointerCancel: end,
      onKeyDown,
      onDoubleClick: reset,
    },
  }
}
